import React, { useMemo, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  useMediaQuery,
} from "@mui/material";
import {
  Inventory,
  Storefront,
  ExpandLess,
  ExpandMore,
  People,
  Description,
  LocalOffer,
  AccountBalance,
} from "@mui/icons-material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const SidebarMenu = ({
  open,
  setOpen,
  submenuOpen,
  setSubmenuOpen,
  navigate,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [accountingOpen, setAccountingOpen] = useState(false);

  const isSuperuser = useMemo(() => localStorage.getItem("is_superuser") === "true", []);
  const isAdmin = useMemo(() => localStorage.getItem("is_admin") === "true", []);
  const isVendedor = useMemo(() => localStorage.getItem("is_vendedor") === "true", []);
  const hasAccess = isSuperuser || isAdmin || isVendedor;
  const canSeeAccounting = isSuperuser || isAdmin;

  const drawerWidth = open ? 240 : 88;

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setOpen(false);
    }
  };

  const handleToggleInventory = () => {
    setSubmenuOpen(!submenuOpen);
  };

  const handleToggleAccounting = () => {
    setAccountingOpen(!accountingOpen);
  };

  const renderText = (label) =>
    open ? (
      <ListItemText
        primary={label}
        sx={{
          ml: 2,
          color: "#fff",
          whiteSpace: "nowrap",
          "& .MuiTypography-root": {
            fontSize: "0.95rem",
            fontWeight: 500,
          },
        }}
      />
    ) : (
      <Box
        sx={{
          fontSize: "0.66rem",
          textAlign: "center",
          color: "#fff",
          width: "100%",
          mt: 0.5,
          mb: 0.2,
          lineHeight: 1.15,
          px: 0.4,
        }}
      >
        {label}
      </Box>
    );

  if (!hasAccess) {
    return null;
  }

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={() => setOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? 280 : drawerWidth,
          boxSizing: "border-box",
          transition: "width 0.3s ease",
          bgcolor: "#1565C0",
          color: "#fff",
          overflowX: "hidden",
          borderRight: "none",
          display: "flex",
          justifyContent: "flex-start",
          boxShadow: isMobile ? "0 20px 45px rgba(0,0,0,0.28)" : "none",
          zIndex: 1400,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          pt: isMobile ? 1.5 : 1,
          pb: 1,
          minHeight: "100%",
        }}
      >
        <List sx={{ width: "100%", py: 0 }}>
          {canSeeAccounting && (
            <>
              <ListItem disablePadding sx={{ justifyContent: "center" }}>
                <ListItemButton
                  onClick={handleToggleAccounting}
                  sx={{
                    bgcolor: accountingOpen ? "#0d47a1" : "inherit",
                    "&:hover": { bgcolor: "#0d47a1" },
                    flexDirection: open ? "row" : "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: open ? 1.15 : 0.9,
                    px: open ? 1.6 : 1,
                    minHeight: 56,
                  }}
                >
                  <AccountBalance sx={{ color: "#fff", fontSize: 24 }} />
                  {renderText("Contabilidad")}
                  {open &&
                    (accountingOpen ? (
                      <ExpandLess sx={{ color: "#fff", ml: "auto" }} />
                    ) : (
                      <ExpandMore sx={{ color: "#fff", ml: "auto" }} />
                    ))}
                </ListItemButton>
              </ListItem>

              <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigate("/admin-panel/admin-cuentas")}
                    sx={{
                      py: 0.85,
                      px: open ? 3 : 1.2,
                      bgcolor: "#0d47a1",
                      "&:hover": { bgcolor: "#0b3c8a" },
                    }}
                  >
                    {renderText("Plan de Cuentas")}
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={() => handleNavigate("/admin-panel/secciones")}
              sx={{
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 1.15 : 0.9,
                px: open ? 1.6 : 1,
                minHeight: 56,
              }}
            >
              <Inventory sx={{ color: "#fff", fontSize: 24 }} />
              {renderText("Secciones")}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={handleToggleInventory}
              sx={{
                bgcolor: submenuOpen ? "#0d47a1" : "inherit",
                "&:hover": { bgcolor: "#0d47a1" },
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 1.15 : 0.9,
                px: open ? 1.6 : 1,
                minHeight: 56,
              }}
            >
              <Storefront sx={{ color: "#fff", fontSize: 24 }} />
              {renderText("Inventario")}
              {open &&
                (submenuOpen ? (
                  <ExpandLess sx={{ color: "#fff", ml: "auto" }} />
                ) : (
                  <ExpandMore sx={{ color: "#fff", ml: "auto" }} />
                ))}
            </ListItemButton>
          </ListItem>

          <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: open ? 3 : 1 }}>
              <ListItemButton
                onClick={() => handleNavigate("/admin-panel/inventario/productos")}
                sx={{
                  py: 0.85,
                  bgcolor: "#0d47a1",
                  "&:hover": { bgcolor: "#0b3c8a" },
                }}
              >
                {renderText("Productos")}
              </ListItemButton>

              <ListItemButton
                onClick={() => handleNavigate("/admin-panel/inventario/variantes")}
                sx={{
                  py: 0.85,
                  bgcolor: "#0d47a1",
                  "&:hover": { bgcolor: "#0b3c8a" },
                }}
              >
                {renderText("Variantes")}
              </ListItemButton>

              <ListItemButton
                onClick={() => handleNavigate("/admin-panel/inventario/movimientos")}
                sx={{
                  py: 0.85,
                  bgcolor: "#0d47a1",
                  "&:hover": { bgcolor: "#0b3c8a" },
                }}
              >
                {renderText("Movimientos")}
              </ListItemButton>

              <ListItemButton
                onClick={() => handleNavigate("/admin-panel/servicios")}
                sx={{
                  py: 0.85,
                  bgcolor: "#0d47a1",
                  "&:hover": { bgcolor: "#0b3c8a" },
                }}
              >
                {renderText("Servicios")}
              </ListItemButton>
            </List>
          </Collapse>

          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={() => handleNavigate("/admin-panel/proformas")}
              sx={{
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 1.15 : 0.9,
                px: open ? 1.6 : 1,
                minHeight: 56,
              }}
            >
              <Description sx={{ color: "#fff", fontSize: 24 }} />
              {renderText("Proformas")}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={() => handleNavigate("/admin-panel/clientes")}
              sx={{
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 1.15 : 0.9,
                px: open ? 1.6 : 1,
                minHeight: 56,
              }}
            >
              <People sx={{ color: "#fff", fontSize: 24 }} />
              {renderText("Personas")}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={() => handleNavigate("/admin-panel/promociones")}
              sx={{
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 1.15 : 0.9,
                px: open ? 1.6 : 1,
                minHeight: 56,
              }}
            >
              <LocalOffer sx={{ color: "#fff", fontSize: 24 }} />
              {renderText("Promociones")}
            </ListItemButton>
          </ListItem>

          {canSeeAccounting && (
            <ListItem disablePadding sx={{ justifyContent: "center" }}>
              <ListItemButton
                onClick={() => handleNavigate("/admin-panel/bancos")}
                sx={{
                  flexDirection: open ? "row" : "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: open ? 1.15 : 0.9,
                  px: open ? 1.6 : 1,
                  minHeight: 56,
                }}
              >
                <AttachMoneyIcon sx={{ color: "#fff", fontSize: 24 }} />
                {renderText("Bancos")}
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default SidebarMenu;